// frontend/src/pages/WidgetTestPage.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WeatherWidget from '../components/widgets/WeatherWidget';
import ClockWidget from '../components/widgets/ClockWidget';
import RSSWidget from '../components/widgets/RSSWidget';
import StreamingWidget from '../components/widgets/StreamingWidget';
import WebContentWidget from '../components/widgets/WebContentWidget';
import MultiZoneWidget from '../components/widgets/MultiZoneWidget';

const WidgetTestPage = () => {
  const { instanceId } = useParams();
  const { t, i18n } = useTranslation();
  const [widgetInstance, setWidgetInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Detect language from URL parameter or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    const savedLang = localStorage.getItem('i18nextLng');
    
    if (langParam && ['de', 'en', 'fr', 'it'].includes(langParam)) {
      i18n.changeLanguage(langParam);
    } else if (savedLang) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  useEffect(() => {
    if (instanceId) {
      loadWidgetInstance();
    }
  }, [instanceId]);

  const loadWidgetInstance = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api';
      
      // Step 1: Load all activated features
      const featuresResponse = await fetch(`${API_URL}/features`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!featuresResponse.ok) {
        throw new Error(t('common.error'));
      }

      const featuresData = await featuresResponse.json();
      const activatedFeatures = featuresData.features.filter(f => f.isActivated);

      // Step 2: Search for the instance across all activated features
      let foundInstance = null;
      
      for (const feature of activatedFeatures) {
        const instancesResponse = await fetch(
          `${API_URL}/features/${feature.id}/instances`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (instancesResponse.ok) {
          const data = await instancesResponse.json();
          const instance = data.instances.find(inst => inst.id === parseInt(instanceId));
          
          if (instance) {
            // Add widget_type to instance (it's the feature.id)
            foundInstance = {
              ...instance,
              widget_type: feature.id
            };
            break;
          }
        }
      }
      
      if (!foundInstance) {
        throw new Error(t('widgets.errors.widgetNotFound'));
      }

      setWidgetInstance(foundInstance);
    } catch (err) {
      console.error('Error loading widget:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Render the appropriate widget based on widget_type
  const renderWidget = () => {
    if (!widgetInstance) return null;

    switch (widgetInstance.widget_type) {
      case 'weather-widget':
        return <WeatherWidget config={widgetInstance.config} />;
      
      case 'clock-widget':
        return <ClockWidget config={widgetInstance.config} />;
      
      case 'rss-feed-widget':
        return <RSSWidget config={widgetInstance.config} />;
      
      case 'streaming-widget':
        return <StreamingWidget config={widgetInstance.config} />;
      
      case 'web-content-widget':
        return <WebContentWidget config={widgetInstance.config} />;

        case 'multi-zone':
        return <MultiZoneWidget config={widgetInstance.config} screenSize={{ width: window.innerWidth, height: window.innerHeight, fontSizeMultiplier: 1, spacingMultiplier: 1 }} />;
      
      default:
        return (
          <div className="w-screen h-screen flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <p className="text-red-600 text-xl font-semibold mb-2">{t('widgets.errors.unknownType')}</p>
              <p className="text-gray-600">{t('widgets.errors.notSupported', { type: widgetInstance.widget_type })}</p>
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <p className="text-red-600 text-xl font-semibold mb-2">{t('common.error')}</p>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!widgetInstance) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <p className="text-gray-600">{t('widgets.errors.widgetNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen">
      {renderWidget()}
    </div>
  );
};

export default WidgetTestPage;